// Ported from vlookup-dramatic/components/site-footer.tsx by scripts/revamp/port-design.js
import React from "react";
import Link from "./link";
import { useSelector } from "react-redux";
import { ArrowUpRight } from "../lib/icons";
import { site } from "../lib/content";
import { isGuestAuthHref } from "../lib/signup-modal";
import { useLoggedIn } from "../hooks/use-logged-in";
import { ButtonLink } from "./button-link";
import { Logo } from "./logo";
import { SocialIcon } from "./social-icons";

export function cleanSocialLink(raw) {
  if (!raw) return null;
  const val = String(raw).trim();
  if (
    !val ||
    val === "#" ||
    val.toLowerCase() === "null" ||
    val.toLowerCase() === "undefined" ||
    val.toLowerCase().startsWith("javascript:")
  ) {
    return null;
  }
  return val;
}

export function SiteFooter() {
  // Live settings (GET setting, loaded by RevampShell): social links, support
  // email and copyright — the same fields the old footer showed.
  const loggedIn = useLoggedIn();
  const setting = useSelector((s) => s.setting) || {};
  const email = setting.support_email || site.email;
  const socialHref = {
    Facebook: cleanSocialLink(setting.facebook_link),
    X: cleanSocialLink(setting.twitter_link || setting.x_link),
    LinkedIn: cleanSocialLink(setting.linkedin_link),
    YouTube: cleanSocialLink(setting.youtube_link),
    Instagram: cleanSocialLink(setting.instagram_link),
    TikTok: cleanSocialLink(setting.tiktok_link),
    Spotify: cleanSocialLink(setting.spotify_link),
  };
  const socials = site.footer.socials
    .map((s) => ({ ...s, href: socialHref[s.label] }))
    .filter((s) => Boolean(s.href));
  return (
    // No top margin: the FAQ band (or the page's last section) runs straight
    // into the footer and the hairline does the separating — a gap of empty
    // background between two panels just reads as a hole.
    <footer className="border-t border-brand-navy/10 bg-cream-deep text-brand-navy">
      <div className="wrap py-16">
        {!loggedIn && (
          <div className="flex flex-col gap-8 border-b border-brand-navy/10 pb-12 md:flex-row md:items-end md:justify-between">
            <div className="max-w-xl">
              <h2 className="font-display text-4xl font-semibold leading-tight text-heading md:text-5xl">
                Ready to find your mentor — or become one?
              </h2>
            </div>
            <ButtonLink href="/#join" className="group w-fit rounded-full bg-brand-red px-7 py-6 text-base font-bold text-white hover:bg-brand-red-dark">
              Join vLookUp free
              <ArrowUpRight className="size-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </ButtonLink>
          </div>
        )}

        <div className={loggedIn ? "grid gap-10 md:grid-cols-[1.6fr_minmax(0,3fr)]" : "grid gap-10 pt-12 md:grid-cols-[1.6fr_minmax(0,3fr)]"}>
          <div>
            <Logo height={46} />
            <p className="mt-3 max-w-[300px] text-sm leading-relaxed text-muted-foreground">
              {/* QA row 40: "MentorLookUp" in the blurb links to its page */}
              {(() => {
                const i = site.description.indexOf("MentorLookUp");
                if (i < 0) return site.description;
                return (<>
                  {site.description.slice(0, i)}
                  <Link href="/mentorlookup" className="font-semibold text-brand-navy underline decoration-brand-navy/30 underline-offset-2 transition-colors hover:text-brand-red">
                    MentorLookUp
                  </Link>
                  {site.description.slice(i + "MentorLookUp".length)}
                </>);
              })()}
            </p>
            <a href={`mailto:${email}`} className="mt-4 inline-block text-sm font-bold text-brand-navy underline underline-offset-4 transition-colors hover:text-brand-red">
              {email}
            </a>
            {socials.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2.5">
                {socials.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="flex size-10 items-center justify-center rounded-full border border-brand-navy/15 bg-card text-base text-brand-navy/70 transition-colors hover:border-brand-red hover:bg-brand-red hover:text-white"
                  >
                    {/* QA row 18: real icons for every network (no text glyphs) */}
                    <SocialIcon label={s.label} className="size-4" />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="grid gap-10 sm:grid-cols-3">
              {site.footer.groups.map((group) => (<FooterCol key={group.title} title={group.title} links={group.links} />))}
            </div>
            <ul className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-brand-navy/10 pt-5">
              {site.footer.utilityLinks
                .filter((l) => !loggedIn || !isGuestAuthHref(l.href))
                .map((l) => (<li key={l.label}>
                  <Link href={l.href} className="text-sm font-medium text-brand-navy/75 transition-colors hover:text-brand-red">
                    {l.label}
                  </Link>
                </li>))}
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-brand-navy/10 pt-6 text-center text-xs font-medium text-muted-foreground">
          {setting.copy_right || site.copyright}

        </div>
      </div>
    </footer>);
}
function FooterCol({ title, links, }) {
  return (<div>
    <div className="mb-4 text-xs font-bold uppercase tracking-[0.12em] text-brand-navy/50">
      {title}
    </div>
    <ul className="flex flex-col gap-2.5">
      {links.map((l) => (<li key={l.label}>
        <Link href={l.href} className="text-sm font-medium text-brand-navy/75 transition-colors hover:text-brand-red">
          {l.label}
        </Link>
      </li>))}
    </ul>
  </div>);
}
